import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from '../../../axios/interceptor';
import openNotification from '../../../components/notification';

const initialState = {
    sessions: [],
    totalRecords: 0,
    activities: [],
    activityOptions: [],
    runningSessions: [],
    loading: false,
    activityLoading: false,
    kunden: [],
    kundenOptions: [],
    kundenLoading: false,
    settings: { roundingMinutes: 0 },
    settingsLoading: false,
};

export const slice = createSlice({
    name: 'supportTracking',
    initialState,
    reducers: {
        setSessions: (state, action) => { state.sessions = action.payload; },
        setTotalRecords: (state, action) => { state.totalRecords = action.payload; },
        setActivities: (state, action) => { state.activities = action.payload; },
        setActivityOptions: (state, action) => { state.activityOptions = action.payload; },
        setRunningSessions: (state, action) => { state.runningSessions = action.payload; },
        addRunningSession: (state, action) => { state.runningSessions = [action.payload, ...state.runningSessions]; },
        removeRunningSession: (state, action) => { state.runningSessions = state.runningSessions.filter(s => s._id !== action.payload); },
        setLoading: (state, action) => { state.loading = action.payload; },
        setActivityLoading: (state, action) => { state.activityLoading = action.payload; },
        setKunden: (state, action) => { state.kunden = action.payload; },
        setKundenOptions: (state, action) => { state.kundenOptions = action.payload; },
        setKundenLoading: (state, action) => { state.kundenLoading = action.payload; },
        setSettings: (state, action) => { state.settings = action.payload; },
        setSettingsLoading: (state, action) => { state.settingsLoading = action.payload; },
    },
});

// ─── Sessions ────────────────────────────────────────────────────────────────

export const startSession = createAsyncThunk('startSession', (data, { dispatch }) => {
    dispatch(setLoading(true));
    return axios.post('/support-session/start', data).then(res => {
        if (res.data.isSuccess) {
            dispatch(addRunningSession(res.data.record));
            openNotification({ message: 'Sitzung gestartet.', type: 'success' });
            return res.data.record;
        } else {
            openNotification({ message: res.data.message, type: 'error' });
            return null;
        }
    }).catch(err => {
        openNotification({ message: err.message, type: 'error' });
    }).finally(() => dispatch(setLoading(false)));
});

export const stopSession = createAsyncThunk('stopSession', (data, { dispatch }) => {
    return axios.post('/support-session/stop', data).then(res => {
        if (res.data.isSuccess) {
            dispatch(removeRunningSession(data.id));
            openNotification({ message: 'Sitzung gespeichert.', type: 'success' });
            return true;
        } else {
            openNotification({ message: res.data.message, type: 'error' });
            return false;
        }
    }).catch(err => {
        openNotification({ message: err.message, type: 'error' });
    });
});

export const getRunningSession = createAsyncThunk('getRunningSession', (userId, { dispatch }) => {
    axios.get(`/support-session/running?user=${userId}`).then(res => {
        if (res.data.isSuccess) dispatch(setRunningSessions(res.data.records || []));
    }).catch(() => {});
});

export const getSessions = createAsyncThunk('getSessions', (data, { dispatch }) => {
    dispatch(setLoading(true));
    axios.post('/support-session/get', data).then(res => {
        if (res.data.isSuccess) {
            dispatch(setSessions(res.data.records));
            dispatch(setTotalRecords(res.data.totalRecords));
        } else {
            dispatch(setSessions([]));
        }
    }).catch(err => openNotification({ message: err.message, type: 'error' }))
      .finally(() => dispatch(setLoading(false)));
});

export const deleteSession = createAsyncThunk('deleteSession', (id, { dispatch, getState }) => {
    const { supportTracking } = getState();
    axios.delete(`/support-session/remove?id=${id}`).then(res => {
        if (res.data.isSuccess) {
            openNotification({ message: 'Eintrag gelöscht.', type: 'success' });
        }
    }).catch(err => openNotification({ message: err.message, type: 'error' }));
});

// ─── Activities ───────────────────────────────────────────────────────────────

export const getActivities = createAsyncThunk('getActivities', (data, { dispatch }) => {
    dispatch(setActivityLoading(true));
    axios.post('/support-activity/get', data).then(res => {
        if (res.data.isSuccess) dispatch(setActivities(res.data.records));
        else dispatch(setActivities([]));
    }).catch(err => openNotification({ message: err.message, type: 'error' }))
      .finally(() => dispatch(setActivityLoading(false)));
});

export const getActivityOptions = createAsyncThunk('getActivityOptions', (organizer, { dispatch }) => {
    axios.get(`/support-activity/listing?organizer=${organizer}`).then(res => {
        if (res.data.isSuccess) dispatch(setActivityOptions(res.data.records));
    }).catch(() => {});
});

export const createActivity = createAsyncThunk('createActivity', (data, { dispatch }) => {
    dispatch(setActivityLoading(true));
    return axios.post('/support-activity/insert', data).then(res => {
        if (res.data.isSuccess) {
            openNotification({ message: res.data.message, type: 'success' });
            dispatch(getActivities({ organizer: data.organizer }));
            return true;
        } else {
            openNotification({ message: res.data.message, type: 'error' });
            return false;
        }
    }).catch(err => openNotification({ message: err.message, type: 'error' }))
      .finally(() => dispatch(setActivityLoading(false)));
});

export const updateActivity = createAsyncThunk('updateActivity', (data, { dispatch }) => {
    dispatch(setActivityLoading(true));
    return axios.post('/support-activity/update', data).then(res => {
        if (res.data.isSuccess) {
            openNotification({ message: res.data.message, type: 'success' });
            dispatch(getActivities({ organizer: data.organizer }));
            return true;
        } else {
            openNotification({ message: res.data.message, type: 'error' });
            return false;
        }
    }).catch(err => openNotification({ message: err.message, type: 'error' }))
      .finally(() => dispatch(setActivityLoading(false)));
});

export const removeActivity = createAsyncThunk('removeActivity', (data, { dispatch }) => {
    axios.delete(`/support-activity/remove?id=${data.id}`).then(res => {
        if (res.data.isSuccess) {
            openNotification({ message: res.data.message, type: 'success' });
            dispatch(getActivities({ organizer: data.organizer }));
        }
    }).catch(err => openNotification({ message: err.message, type: 'error' }));
});

export const getKunden = createAsyncThunk('getKunden', (data, { dispatch }) => {
    dispatch(setKundenLoading(true));
    axios.post('/support-kunde/get', data).then(res => {
        if (res.data.isSuccess) dispatch(setKunden(res.data.records));
        else dispatch(setKunden([]));
    }).catch(err => openNotification({ message: err.message, type: 'error' }))
      .finally(() => dispatch(setKundenLoading(false)));
});

export const getKundenOptions = createAsyncThunk('getKundenOptions', (organizer, { dispatch }) => {
    axios.get(`/support-kunde/listing?organizer=${organizer}`).then(res => {
        if (res.data.isSuccess) dispatch(setKundenOptions(res.data.records));
    }).catch(() => {});
});

export const createKunde = createAsyncThunk('createKunde', (data, { dispatch }) => {
    dispatch(setKundenLoading(true));
    return axios.post('/support-kunde/insert', data).then(res => {
        if (res.data.isSuccess) {
            openNotification({ message: res.data.message, type: 'success' });
            dispatch(getKunden({ organizer: data.organizer }));
            return true;
        } else {
            openNotification({ message: res.data.message, type: 'error' });
            return false;
        }
    }).catch(err => openNotification({ message: err.message, type: 'error' }))
      .finally(() => dispatch(setKundenLoading(false)));
});

export const updateKunde = createAsyncThunk('updateKunde', (data, { dispatch }) => {
    dispatch(setKundenLoading(true));
    return axios.post('/support-kunde/update', data).then(res => {
        if (res.data.isSuccess) {
            openNotification({ message: res.data.message, type: 'success' });
            dispatch(getKunden({ organizer: data.organizer }));
            return true;
        } else {
            openNotification({ message: res.data.message, type: 'error' });
            return false;
        }
    }).catch(err => openNotification({ message: err.message, type: 'error' }))
      .finally(() => dispatch(setKundenLoading(false)));
});

export const removeKunde = createAsyncThunk('removeKunde', (data, { dispatch }) => {
    axios.delete(`/support-kunde/remove?id=${data.id}`).then(res => {
        if (res.data.isSuccess) {
            openNotification({ message: res.data.message, type: 'success' });
            dispatch(getKunden({ organizer: data.organizer }));
        }
    }).catch(err => openNotification({ message: err.message, type: 'error' }));
});

export const importKunden = createAsyncThunk('importKunden', (data, { dispatch }) => {
    dispatch(setKundenLoading(true));
    return axios.post('/support-kunde/import', data).then(res => {
        if (res.data.isSuccess) {
            openNotification({ message: res.data.message, type: 'success' });
            dispatch(getKunden({ organizer: data.organizer }));
            return true;
        } else {
            openNotification({ message: res.data.message, type: 'error' });
            return false;
        }
    }).catch(err => openNotification({ message: err.message, type: 'error' }))
      .finally(() => dispatch(setKundenLoading(false)));
});

export const getSettings = createAsyncThunk('getSettings', (organizer, { dispatch }) => {
    axios.get(`/support-settings/get?organizer=${organizer}`).then(res => {
        if (res.data.isSuccess) dispatch(setSettings(res.data.record));
    }).catch(() => {});
});

export const updateSettings = createAsyncThunk('updateSettings', (data, { dispatch }) => {
    dispatch(setSettingsLoading(true));
    return axios.post('/support-settings/update', data).then(res => {
        if (res.data.isSuccess) {
            openNotification({ message: res.data.message, type: 'success' });
            dispatch(setSettings({ roundingMinutes: data.roundingMinutes }));
            return true;
        } else {
            openNotification({ message: res.data.message, type: 'error' });
            return false;
        }
    }).catch(err => openNotification({ message: err.message, type: 'error' }))
      .finally(() => dispatch(setSettingsLoading(false)));
});

export const {
    setSessions, setTotalRecords, setActivities, setActivityOptions,
    setRunningSessions, addRunningSession, removeRunningSession,
    setLoading, setActivityLoading,
    setKunden, setKundenOptions, setKundenLoading, setSettings, setSettingsLoading,
} = slice.actions;

export default slice.reducer;
